/*
 * Copyright 2021 Mia srl
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {useEffect, useState} from 'react'
import MainRouting from './pages/mainRouting/MainRouting'
import ThemeAppProvider from './components/layouts/ThemeAppProvider/ThemeAppProvider'
import {CssBaseline} from '@mui/material'
import {Route} from 'react-router-dom'
import {useAppSelector} from 'src/hooks'
import {applyDynamicManifest} from 'src/services/pwa/manifestManager'
import apiClient from 'src/services/apiClient'

function App () {
  const pspData = useAppSelector(state => state.pspConfigReducer.data)
  const [loginBranding, setLoginBranding] = useState<{title?: string; icon?: string; logoPSP?: string}>()

  // login-configuration is host-scoped and available pre-auth (no session/scope-cookie
  // dependency), so it resolves the PWA install title/icon much earlier and more
  // reliably than psp-config, which requires a bootstrapped session.
  useEffect(() => {
    let mounted = true
    apiClient.loginConfiguration().then((res: any) => {
      if (mounted) {
        setLoginBranding({title: res?.PSPTitleAndIcon?.title, icon: res?.PSPTitleAndIcon?.icon, logoPSP: res?.logoPSP})
      }
    }).catch(() => {})
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    const hasBranding = loginBranding?.title || loginBranding?.logoPSP || loginBranding?.icon ||
      pspData.logoImagePSP || pspData.logoPSP
    if (hasBranding) {
      applyDynamicManifest({
        ...pspData,
        PSPTitleAndIcon: {title: loginBranding?.title, icon: loginBranding?.icon},
        logoPSP: loginBranding?.logoPSP || pspData.logoPSP,
      })
    }
  }, [pspData, loginBranding])

  return (
    <>
      <CssBaseline />
      <ThemeAppProvider>
        <Route path='/'>
          <MainRouting />
        </Route>
      </ThemeAppProvider>
    </>
  )
}

export default App
